/**
 * Portfolio mix widget — a donut ring of owned properties by strategy with a
 * legend. Replicates the design's SVG arc math (one arc per strategy, sized by
 * its share of owned properties, with the gap centered on each joint).
 */
import { DashboardCard } from "./DashboardCard";
import { fmtMoneyShort, type MixSlice } from "./dashboard-metrics";

interface PortfolioMixCardProps {
  slices: MixSlice[];
  total: number;
}

const SIZE = 108;
const SW = 10;
const R = (SIZE - SW) / 2;
const C = 2 * Math.PI * R;

export function PortfolioMixCard({ slices, total }: PortfolioMixCardProps) {
  const gap = slices.length > 1 ? 2 : 0;
  let offset = 0;
  const arcs = slices.map((s) => {
    const len = (s.count / total) * C;
    const drawn = Math.max(len - gap, 1);
    const arc = (
      <circle
        key={s.key}
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={R}
        fill="none"
        strokeWidth={SW}
        strokeLinecap="butt"
        style={{ stroke: s.color }}
        strokeDasharray={`${drawn} ${C - drawn}`}
        strokeDashoffset={-(offset + gap / 2)}
      />
    );
    offset += len;
    return arc;
  });

  return (
    <DashboardCard title="Portfolio mix" subtitle="Owned properties by strategy">
      {total === 0 ? (
        <div className="py-[18px] text-center text-[13px] text-muted-foreground">
          No owned properties yet.
        </div>
      ) : (
        <div className="flex items-center gap-6">
          <div className="relative h-[108px] w-[108px] flex-shrink-0">
            <svg
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="block -rotate-90"
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={R}
                fill="none"
                strokeWidth={SW}
                className="stroke-muted"
              />
              {arcs}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[30px] font-extrabold leading-none text-foreground">
                {total}
              </div>
              <div className="mt-1 text-[10px] font-bold tracking-[0.08em] text-muted-foreground">
                OWNED
              </div>
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {slices.map((s) => (
              <div
                key={s.key}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-[11px]"
              >
                <span
                  className="h-[11px] w-[11px] flex-shrink-0 rounded-[3px]"
                  style={{ background: s.color }}
                />
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {s.label}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                    {s.count} {s.count === 1 ? "property" : "properties"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold tabular-nums text-foreground">
                    {fmtMoneyShort(s.vol)}
                  </div>
                  <div className="mt-0.5 text-[11.5px] tabular-nums text-muted-foreground">
                    {s.pct}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}
